// @ts-check
import { render } from 'preact'
import { signal, effect } from '@preact/signals'
import showErrorPopup from '@theme/show-error-popup'
import ky from 'ky'
import {
  API_ENDPOINTS,
  API_HEALTH_CHECK_POST_CODE,
  GEO_LOCATION_OPTIONS,
  COMMANDS,
  RESULTS,
  ERRORS,
} from '@theme/constants'
import * as v from 'valibot'
import toggleLoadingOverlay from '@theme/loading-overlay'
import locationsView from '@theme/locations-view'

// Valibot schemas for API response validation
/** @type {v.ObjectSchema<ServiceArea>} */
const ServiceAreaSchema = v.object({
  placeName: v.string(),
  placeId: v.string(),
})

/** @type {v.ObjectSchema<Service>} */
const ServiceSchema = v.object({
  displayName: v.string(),
  description: v.string(),
})

/** @type {v.ObjectSchema<RetailLocationDistance>} */
const RetailLocationDistanceSchema = v.object({
  retail_location: v.number(),
  distance_kms: v.number(),
})

/** @type {v.ObjectSchema<RetailLocation>} */
const RetailLocationSchema = v.object({
  id: v.number(),
  created_at: v.number(),
  Active: v.boolean(),
  store_code: v.string(),
  Company: v.string(),
  State: v.string(),
  regions_id: v.number(),
  Name: v.string(),
  GBP_Descriptions: v.string(),
  Opening_Dates: v.array(v.string()),
  Opening_Days: v.string(),
  Opening_Hours_Weekday: v.string(),
  Open_Weekday: v.string(),
  Close_Weekday: v.string(),
  Opening_Hours_Weekend: v.string(),
  Open_Weekend: v.string(),
  Close_Weekend: v.string(),
  Address: v.string(),
  Google_maps_url: v.string(),
  phone: v.string(),
  Google_Review_URL: v.string(),
  google_locations_id: v.number(),
  Longitude: v.string(),
  Latitude: v.string(),
  Square_id: v.string(),
  Service_Areas: v.array(ServiceAreaSchema),
  Services_list: v.array(ServiceSchema),
  Distance_kms: v.number(),
})

/** @type {v.ObjectSchema<PostCode>} */
const PostCodeSchema = v.object({
  id: v.number(),
  created_at: v.number(),
  Post_Code: v.number(),
  Name: v.string(),
  Delivery: v.boolean(),
  Retail: v.boolean(),
  Marketing: v.boolean(),
  delivery_routes_id: v.number(),
  regions_id: v.number(),
  retail_locations_id: v.number(),
  collection_region_id: v.number(),
  longitude: v.string(),
  latitude: v.string(),
  State: v.string(),
  SA3: v.string(),
  SA4: v.string(),
  GCCSA: v.string(),
  retail_locations_distance: v.array(RetailLocationDistanceSchema),
})

/** @type {v.ObjectSchema<Region>} */
const RegionSchema = v.object({
  id: v.number(),
  created_at: v.number(),
  Region: v.string(),
  Product_handle: v.string(),
  Location_URL: v.string(),
  Click_Collect_Available: v.boolean(),
  State: v.string(),
})

/** @type {v.ObjectSchema<DeliveryRoutes>} */
const DeliveryRoutesSchema = v.object({
  id: v.number(),
  created_at: v.number(),
  regions_id: v.number(),
  Route_Name: v.string(),
  Delivery_dates: v.array(v.string()),
  Delivery_charge: v.number(),
  Delivery_charge_handle: v.string(),
  Delivery_charge_variantId: v.string(),
})

/** @type {v.ObjectSchema<ApiResponse>} */
const ApiResponseSchema = v.object({
  Post_Code: PostCodeSchema,
  Region: RegionSchema,
  Delivery_Routes: DeliveryRoutesSchema,
  Retail_Locations: v.array(RetailLocationSchema),
})

// Valibot schemas for geolocation API response validation

/** @type {v.ObjectSchema<StockBalance>} */
const StockBalanceSchema = v.object({
  tree_count: v.number(),
  comment: v.string(),
  last_updated: v.number(),
  Availability: v.string(),
})

/** @type {v.ObjectSchema<RegionInfo>} */
const RegionInfoSchema = v.object({
  Region: v.string(),
  Product_handle: v.string(),
  Location_URL: v.string(),
  Click_Collect_Available: v.boolean(),
})

/** @type {v.ObjectSchema<GeolocationRetailLocation>} */
const GeolocationRetailLocationSchema = v.object({
  id: v.number(),
  store_code: v.string(),
  State: v.string(),
  regions_id: v.number(),
  Name: v.string(),
  Opening_Dates: v.array(v.string()),
  Opening_Days: v.string(),
  Opening_Hours_Weekday: v.string(),
  Opening_Hours_Weekend: v.string(),
  Address: v.string(),
  Google_maps_url: v.string(),
  phone: v.string(),
  Google_Review_URL: v.string(),
  Longitude: v.string(),
  Latitude: v.string(),
  region: RegionInfoSchema,
  stock_balance: StockBalanceSchema,
  Distance_kms: v.array(v.number()),
})

/** @type {v.ObjectSchema<GeolocationPostCode>} */
const GeolocationPostCodeSchema = v.object({
  Post_Code: v.number(),
  Name: v.string(),
  Delivery: v.boolean(),
  Retail: v.boolean(),
  regions_id: v.number(),
  retail_locations_id: v.number(),
  longitude: v.string(),
  latitude: v.string(),
  State: v.string(),
  retail_locations_distance: v.array(RetailLocationDistanceSchema),
})

/** @type {v.ObjectSchema<GeolocationApiResponse>} */
const GeolocationApiResponseSchema = v.object({
  Post_Code: GeolocationPostCodeSchema,
  Retail_Locations: v.array(GeolocationRetailLocationSchema),
})

class LocationFinder extends HTMLElement {
  /**
   * Constructs the LocationFinder custom element.
   * @constructor
   */
  constructor() {
    super()
    this.state = {
      postCode: signal(''),
      retailLocations: signal([]),
      isLoading: signal(false),
      error: signal(null),
      landingMapActive: signal(true),
      locationsSectionActive: signal(false),
      inputValue: signal(''),
      resultsWarningActive: signal(false),
      resultsWarningText: signal(''),
      selectedLocation: signal({}),
      selectedLocationId: signal(null),
      locationsJustFetched: signal(false),
      /* The searched postcode's serviceability (the response's Post_Code object:
         Delivery / Retail / Post_Code). Click & collect is decided per store from
         loc.region.Click_Collect_Available, but DELIVERY is a property of the
         postcode, not the store, and it was being thrown away -- the FOUND
         listener kept only Retail_Locations. Held here so the store cards can
         offer the right CTA without a second request. */
      serviceability: signal(null),
    }

    /** @type {HTMLFormElement | null} The form element for postcode search */
    this.form = null

    /** @type {HTMLInputElement | null} The postcode input field */
    this.postalCodeInput = null

    /** @type {HTMLButtonElement | null} The geolocation button */
    this.geolocationButton = null

    /** @type {HTMLSpanElement | null} The result warning container */
    this.resultsWarning = null

    /** @type {HTMLElement | null} The map section container */
    this.mapSection = null

    /** @type {HTMLElement | null} The locations section container */
    this.locationsSection = null
  }

  /**
   * Lifecycle method called when the element is added to the DOM.
   * Initializes static elements and listeners.
   */
  async connectedCallback() {
    // A failed check used to stop the page and tell the customer the service
    // was down. Twice in October the API was fine and the phone's request was
    // simply slow or lost (2026-10-21/22), so start anyway: a search that
    // really fails shows its own error.
    const isHealthy = await this.checkApiHealth()
    if (!isHealthy) console.warn('LocationFinder: API health check failed; starting anyway')

    this.setStaticElements()
    this.setEventListeners()
    this.setupEventDispatcher()
    this.setupEffects()
    toggleLoadingOverlay(this, this.state.isLoading)
    this.initFromUrl()
  }

  /**
   * Sets static elements by querying the DOM for data-component attributes.
   * Uses defensive checks to ensure elements exist before assignment.
   */
  setStaticElements() {
    const form = this.querySelector('[data-component-location-finder-form]')
    if (form instanceof HTMLFormElement) {
      this.form = form
    }

    const postalCodeInput = this.querySelector('[data-component-postal-code-input]')
    if (postalCodeInput instanceof HTMLInputElement) {
      this.postalCodeInput = postalCodeInput
    }

    const geolocationButton = this.querySelector('[data-component-geolocation-button]')
    if (geolocationButton instanceof HTMLButtonElement) {
      this.geolocationButton = geolocationButton
    }

    /** @type {HTMLSpanElement | null} */
    const resultsWarning = this.querySelector('[data-component-results-warning]')
    if (resultsWarning instanceof HTMLSpanElement) {
      this.resultsWarning = resultsWarning
    }

    /** @type {HTMLElement | null} */
    const mapSection = this.querySelector('[data-component-map-section]')
    if (mapSection) {
      this.mapSection = mapSection
    }

    /** @type {HTMLElement | null} */
    const locationsSection = this.querySelector('[data-component-locations]')
    if (locationsSection) {
      this.locationsSection = locationsSection
    }
  }

  /**
   * Sets up event listeners for form submission and input changes.
   */
  setEventListeners() {
    if (!this.form) {
      console.error('Form element not found')
      showErrorPopup('Form element not found')
      return
    }

    this.form.addEventListener('submit', this.handleSubmit.bind(this))

    if (this.geolocationButton) {
      this.geolocationButton.addEventListener('click', () => {
        this.dispatchEvent(
          new CustomEvent(COMMANDS.LOCATION.REQUEST_GEOLOCATION, {
            bubbles: true,
            composed: true,
          }),
        )
      })
    }

    if (this.postalCodeInput) {
      this.postalCodeInput.addEventListener('input', this.handleInputChange.bind(this))
    }

    this.addEventListener(COMMANDS.LOCATION.CLEAR_RESULTS, () => {
      this.state.postCode.value = ''
      this.state.retailLocations.value = []
      this.state.serviceability.value = null
      this.state.selectedLocationId.value = null
      this.state.selectedLocation.value = {}
      this.state.landingMapActive.value = true
      this.state.locationsSectionActive.value = false
      this.state.resultsWarningActive.value = false
    })
  }

  setupEventDispatcher() {
    // Loading state
    this.addEventListener(RESULTS.LOCATION.LOADING, (e) => {
      this.state.isLoading.value = e.detail.isLoading
    })

    // Command Handlers
    this.addEventListener(COMMANDS.LOCATION.FIND_BY_COORDINATES, (e) => {
      this.handleFindByCoordinatesCommand(e.detail)
    })

    this.addEventListener(COMMANDS.LOCATION.FIND_BY_POSTCODE, (e) => {
      this.handleFindByPostcodeCommand(e.detail)
    })

    this.addEventListener(COMMANDS.LOCATION.REQUEST_GEOLOCATION, () => {
      this.requestGeolocation()
    })

    // Result Listeners
    this.addEventListener(RESULTS.LOCATION.FOUND, (e) => {
      try {
        const data = e.detail.data
        const postCode = data.Post_Code.Post_Code
        this.state.postCode.value = postCode.toString()
        this.state.serviceability.value = data.Post_Code

        // A SUCCESSFUL response with no stores is a real case, not an impossible
        // one: a postcode whose retail flag still points at a store that is not
        // trading (Sunshine Coast today) answers HTTP 200 with an empty list.
        // Without this branch the page rendered nothing at all -- no cards, no
        // warning, no explanation -- which reads as a broken search rather than
        // "we don't come here". Jeremy 2026-09-22: show the out-of-zone message.
        const stores = data.Retail_Locations || []
        if (!stores.length && data.Post_Code?.Delivery !== true) {
          this.state.retailLocations.value = []
          this.state.locationsSectionActive.value = false
          this.state.landingMapActive.value = true
          this.state.resultsWarningText.value = ERRORS.INVALID_POSTCODE
          this.state.resultsWarningActive.value = true
          return
        }

        this.state.retailLocations.value = stores

        // A store pin on the region map carries its id through the command's
        // metadata, so the list can open on THAT store instead of defaulting to
        // the first result (renderLocationsView does that when
        // locationsJustFetched is true, so this clears the flag it reads).
        const wantedStoreId = e.detail?.metadata?.storeId
        const chosen = wantedStoreId ? stores.find((s) => s.id === wantedStoreId) : null
        if (chosen) {
          this.state.selectedLocationId.value = chosen.id
          this.state.selectedLocation.value = chosen
          this.state.locationsJustFetched.value = false
        } else {
          this.state.locationsJustFetched.value = true
        }
      } catch (error) {
        showErrorPopup(error)
      }
    })

    this.addEventListener(RESULTS.LOCATION.ERROR, (e) => {
      const error = e.detail.error
      const metadata = e.detail.metadata
      const errorType = metadata.errorType
      // Error objects have no own enumerable props — carry the message explicitly.
      this.state.error.value = {
        ...error,
        message: error?.message ?? String(error),
        errorType,
      }
    })
  }

  // Effects
  setupEffects() {
    // Postal code input sync effect
    this._postCodeEffect = effect(() => {
      if (this.postalCodeInput && this.state.postCode.value !== this.postalCodeInput.value) {
        this.postalCodeInput.value = this.state.postCode.value
      }
    })

    // Retail locations effect, render location view when locations isn't empty
    this._retailLocationsEffect = effect(() => {
      if (this.state.retailLocations.value.length > 0) {
        this.renderLocationsView()
      }
    })

    // Landing map active effect
    this._landingMapActiveEffect = effect(() => {
      if (this.state.landingMapActive.value) {
        // The warning is cleared by the callers that return to the landing map
        // (CLEAR_RESULTS), not here: the out-of-zone path shows the map AND the
        // warning together, and effect ordering inside a batch would otherwise
        // hide the warning it just raised.
        this.mapSection?.classList.remove('hidden')
      } else {
        this.mapSection?.classList.add('hidden')
      }
    })

    // Locations section active effect
    this._locationsSectionActiveEffect = effect(() => {
      if (this.state.locationsSectionActive.value) {
        this.state.resultsWarningActive.value = false
        this.locationsSection?.classList.remove('hidden')
      } else {
        this.locationsSection?.classList.add('hidden')
      }
    })

    // Results warning effect
    this._resultsWarningEffect = effect(() => {
      if (this.state.resultsWarningActive.value) {
        this.resultsWarning?.classList.remove('hidden')
        this.resultsWarning?.removeAttribute('aria-hidden')
      } else {
        this.resultsWarning?.classList.add('hidden')
        this.resultsWarning?.setAttribute('aria-hidden', 'true')
      }

      if (this.resultsWarning instanceof HTMLSpanElement) {
        // InnerHtml is used to all anchor tag, and save time of creating anchor tag
        this.resultsWarning.innerHTML = this.state.resultsWarningText.value
      }
    })

    // Error effect
    this._errorEffect = effect(() => {
      if (!this.state.error.value) return

      const errorType = this.state.error.value?.errorType

      if (errorType === 500) {
        // Order matters: switching the landing map on clears the warning (see the
        // landing-map effect), so raise the warning last or an out-of-zone search
        // straight after a successful one shows nothing.
        this.state.locationsSectionActive.value = false
        this.state.landingMapActive.value = true
        this.state.resultsWarningText.value = ERRORS.INVALID_POSTCODE
        this.state.resultsWarningActive.value = true
      } else {
        // A human message (geolocation denied / unavailable / timed out) reads
        // better than the raw state object serialised as JSON.
        const { message } = this.state.error.value
        showErrorPopup(typeof message === 'string' && message ? message : this.state.error.value)
      }
    })

    // Loading effect
    this._isLoadingEffect = effect(() => {
      if (this.locationsSection) {
        this.locationsSection.setAttribute('aria-busy', this.state.isLoading.value)
      }
    })
  }

  renderLocationsView() {
    try {
      this.state.landingMapActive.value = false

      const currentLocations = this.state.retailLocations.value

      // Reset to the first location if locations were just fetched
      if (this.state.locationsJustFetched.value && currentLocations.length > 0) {
        this.state.selectedLocationId.value = currentLocations[0].id
        this.state.selectedLocation.value = currentLocations[0]
        this.state.locationsJustFetched.value = false
      }

      /**
       *
       * @param {Location}  location
       * @returns void
       */
      const handleLocationSelect = (location) => {
        if (this.state.selectedLocationId.value === location?.id) {
          this.state.selectedLocationId.value = null
          this.state.selectedLocation.value = {}
        } else {
          this.state.selectedLocationId.value = location?.id || null
          this.state.selectedLocation.value = location || {}
        }
        this.renderLocationsView()
      }
      render(
        locationsView(this.state.retailLocations.value, {
          selectedId: this.state.selectedLocationId.value,
          onSelect: handleLocationSelect,
          serviceability: this.state.serviceability.value,
        }),
        this.locationsSection,
      )

      this.state.locationsSectionActive.value = true
    } catch (error) {
      showErrorPopup(error)
    }
  }

  /**
   * Handles coordinate search commands
   * @param {FindByCoordinatesCommand['detail']} detail - Event detail with coordinates/metadata
   */
  async handleFindByCoordinatesCommand({ coordinates, metadata }) {
    try {
      this.dispatchEvent(
        new CustomEvent(RESULTS.LOCATION.LOADING, {
          detail: { isLoading: true, metadata },
        }),
      )

      // Use test coordinates if none provided (dev mode)
      /*       const { latitude, longitude } = {
        latitude: -37.74791716485911,
        longitude: 144.99620680035153,
      } */

      // real coordinates
      const { latitude, longitude } = coordinates

      const response = await ky.post(API_ENDPOINTS.RETAIL_LOCATIONS, {
        json: { Lattitude: latitude, Longtitude: longitude },
        timeout: 10000,
      })

      const data = v.parse(GeolocationApiResponseSchema, await response.json())

      this.dispatchEvent(
        new CustomEvent(RESULTS.LOCATION.FOUND, {
          detail: {
            data,
            metadata: { ...metadata, coordinates: { latitude, longitude } },
          },
        }),
      )
    } catch (error) {
      const errorType = error?.response ? error?.response.status : 'unknown'

      this.dispatchEvent(
        new CustomEvent(RESULTS.LOCATION.ERROR, {
          detail: {
            error,
            metadata: { ...metadata, errorType },
          },
        }),
      )
    } finally {
      this.dispatchEvent(
        new CustomEvent(RESULTS.LOCATION.LOADING, {
          detail: { isLoading: false, metadata },
        }),
      )
    }
  }

  /**
   * Handles postcode search commands
   * @param {FindByPostcodeCommand['detail']} detail - Event detail with postcode/metadata
   */
  async handleFindByPostcodeCommand({ postcode, metadata }) {
    try {
      this.dispatchEvent(
        new CustomEvent(RESULTS.LOCATION.LOADING, {
          detail: { isLoading: true, metadata },
        }),
      )

      const response = await ky.post(API_ENDPOINTS.RETAIL_LOCATIONS, {
        json: { post_code: postcode },
        timeout: 10000,
      })

      const data = v.parse(GeolocationApiResponseSchema, await response.json())
      this.dispatchEvent(
        new CustomEvent(RESULTS.LOCATION.FOUND, {
          detail: {
            data,
            metadata: { ...metadata, postcode: postcode },
          },
        }),
      )
    } catch (error) {
      const errorType = error?.response ? error?.response.status : 'unknown'

      this.dispatchEvent(
        new CustomEvent(RESULTS.LOCATION.ERROR, {
          detail: {
            error,
            metadata: { ...metadata, errorType },
          },
        }),
      )
    } finally {
      this.dispatchEvent(
        new CustomEvent(RESULTS.LOCATION.LOADING, {
          detail: { isLoading: false, metadata },
        }),
      )
    }
  }

  /**
   * Handles form submission for postcode search.
   * @param {Event} event - The form submit event
   */
  handleSubmit(event) {
    event.preventDefault()

    const postcode = this.state.inputValue.value.trim()

    // Clear results if input is empty
    if (!postcode) {
      this.dispatchEvent(
        new CustomEvent(COMMANDS.LOCATION.CLEAR_RESULTS, {
          detail: {
            metadata: { source: 'form-submit-blank' },
          },
          bubbles: true,
          composed: true,
        }),
      )
      return
    }

    const isValidLength = postcode.length == 4

    // Validate postcode is exactly 4 digits
    if (!isValidLength) {
      this.state.resultsWarningActive.value = true
      this.state.resultsWarningText.value = 'Please enter a 4-digit postcode.'
      this.updateAriaAttributes(true)
      return
    }

    this.state.resultsWarningActive.value = false
    this.state.resultsWarningText.value = ''
    this.updateAriaAttributes()

    this.updateUrlWithPostcode(postcode)

    this.dispatchEvent(
      new CustomEvent(COMMANDS.LOCATION.FIND_BY_POSTCODE, {
        detail: {
          postcode: postcode,
          metadata: { source: 'form-submit' },
        },
        bubbles: true,
        composed: true,
      }),
    )
  }

  /**
   * Handles input change events on the postcode input field.
   * @param {Event} event - The input event
   */
  handleInputChange(event) {
    this.state.inputValue.value = event?.target?.value

    // A SUBURB NAME is legitimate input now: the suggestion list resolves it to a
    // postcode and writes that back here before submit, so this guard must not
    // shout "only numbers" at every keystroke of "South Yarra". Only complain
    // about a value that is trying to BE a postcode and failing -- "3141x".
    const inputValue = this.state.inputValue.value.trim()
    const malformedPostcode = /\d/.test(inputValue) && !/^\d*$/.test(inputValue)

    if (inputValue && malformedPostcode) {
      this.state.resultsWarningActive.value = true
      this.state.resultsWarningText.value = 'Please enter only numbers for the postcode.'
      this.postalCodeInput?.setAttribute('aria-invalid', 'true')
    } else {
      this.state.resultsWarningActive.value = false
      this.state.resultsWarningText.value = ''
      this.postalCodeInput?.setAttribute('aria-invalid', 'false')
    }
    this.updateAriaAttributes()
  }

  /**
   * Handles the geolocation button click event.
   * Requests the user's current location using the Geolocation API.
   */
  async requestGeolocation() {
    // Dispatch loading event
    this.dispatchEvent(
      new CustomEvent(RESULTS.LOCATION.LOADING, {
        detail: { isLoading: true },
      }),
    )

    if (!navigator.geolocation) {
      this.dispatchEvent(
        new CustomEvent(RESULTS.LOCATION.ERROR, {
          detail: {
            error: new Error('Geolocation not supported'),
            metadata: { source: 'geolocation-api' },
          },
        }),
      )
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.dispatchEvent(
          new CustomEvent(COMMANDS.LOCATION.FIND_BY_COORDINATES, {
            detail: {
              coordinates: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              },
              metadata: {
                source: 'geolocation-api',
                accuracy: position.coords.accuracy,
              },
            },
          }),
        )
      },
      (error) => {
        // A GeolocationPositionError has no own enumerable properties, so
        // spreading it into state produced an empty "Error Details" modal.
        // Turn it into a real Error with a human message (denied / unavailable
        // / timed out) so the customer knows to type their postcode instead.
        const messages = {
          1: 'Location access was denied. Please allow location access or enter your postcode above.',
          2: 'Your location is unavailable right now. Please enter your postcode above.',
          3: 'Finding your location timed out. Please try again or enter your postcode above.',
        }
        this.dispatchEvent(
          new CustomEvent(RESULTS.LOCATION.ERROR, {
            detail: {
              error: new Error(messages[error?.code] ?? 'Unable to find your location. Please enter your postcode above.'),
              metadata: { source: 'geolocation-api', geolocationCode: error?.code },
            },
          }),
        )
      },
      GEO_LOCATION_OPTIONS,
    )
  }

  /**
   * Checks if the API is healthy and accessible.
   * Makes a test request to verify the API is responding with valid data.
   * @returns {Promise<boolean>} True if API is healthy and returns valid data, false otherwise
   */
  async checkApiHealth() {
    try {
      const endpoint = API_ENDPOINTS.POST_CODES.replace('${postCode}', API_HEALTH_CHECK_POST_CODE)

      const response = await ky.get(endpoint, {
        timeout: 8000, // phones on a slow connection
        retry: 1,
      })

      if (!response.ok) {
        console.error('API health check failed: Non-OK response')
        return false
      }

      // Parse and validate the response data
      /** @type {ApiResponse} */
      const data = await response.json()
      const validationResult = v.safeParse(ApiResponseSchema, data)

      if (!validationResult.success) {
        console.error('API health check failed: Invalid response structure', validationResult.issues)
        return false
      }

      console.log('API health check passed')
      return true
    } catch (error) {
      console.error('API health check failed:', error)
      return false
    }
  }

  /**
   * Mirrors the input's validity to ARIA. Reads the input itself (not the last
   * found postcode — NT postcodes come back as "800" and would flag a valid
   * search as invalid) and only flags non-numeric input while typing; the
   * 4-digit rule is enforced on submit via `forceInvalid`.
   * @param {boolean} [forceInvalid]
   */
  updateAriaAttributes(forceInvalid = false) {
    if (!this.postalCodeInput) return
    const value = (this.postalCodeInput.value ?? '').trim()
    // A suburb name is legitimate input now. Flagging it aria-invalid while someone
    // types "South Yarra" is the same defect the visible warning had, just moved
    // into the accessibility layer. Only a MALFORMED postcode ("3141x") is invalid
    // here; the 4-digit rule is still enforced on submit via forceInvalid.
    const malformed = /\d/.test(value) && !/^\d*$/.test(value)
    const isValid = !forceInvalid && !malformed
    this.postalCodeInput.setAttribute('aria-invalid', `${!isValid}`)
    if (isValid) {
      this.postalCodeInput.removeAttribute('aria-describedby')
    } else {
      this.postalCodeInput.setAttribute('aria-describedby', 'postal-code-error')
    }
    const errorElement = this.querySelector('[data-component-postal-code-error]')
    if (errorElement) {
      errorElement.textContent = isValid ? '' : this.state.resultsWarningText.value.replace(/<[^>]*>/g, '')
    }
  }

  /**
   * Updates the current URL with the given postcode (as a query param)
   * without reloading the page.
   * @param {string} postcode
   */
  updateUrlWithPostcode(postcode) {
    try {
      const url = new URL(window.location.href)
      if (postcode) {
        url.searchParams.set('postcode', postcode)
      } else {
        url.searchParams.delete('postcode')
      }
      // Use replaceState so we don't bloat history for each keypress/submit
      window.history.replaceState({}, '', url.toString())
    } catch (e) {
      console.warn('Failed to update URL with postcode', e)
    }
  }

  /**
   * Reads a postcode from the URL (if present and valid) and triggers
   * the same flow as a normal postcode search.
   */
  initFromUrl() {
    try {
      const url = new URL(window.location.href)
      const postcodeFromUrl = url.searchParams.get('postcode') || ''

      const trimmed = postcodeFromUrl.trim()

      if (!trimmed) return

      // Basic validation: numeric and length 4, to avoid spamming the API
      const isNumeric = /^\d+$/.test(trimmed)
      const isValidLength = trimmed.length === 4

      if (!isNumeric || !isValidLength) {
        return
      }

      // Prime state + input
      this.state.inputValue.value = trimmed
      this.state.postCode.value = trimmed
      if (this.postalCodeInput) {
        this.postalCodeInput.value = trimmed
      }

      // Trigger the same command you'd send on a valid submit
      this.dispatchEvent(
        new CustomEvent(COMMANDS.LOCATION.FIND_BY_POSTCODE, {
          detail: {
            postcode: trimmed,
            metadata: { source: 'url-init' },
          },
          bubbles: true,
          composed: true,
        }),
      )
    } catch (e) {
      console.warn('Failed to init postcode from URL', e)
    }
  }
}

customElements.define('location-finder', LocationFinder)
